// Import package components
import { createContext, useRef, useState } from "react";
import { useCookies } from "react-cookie";

import AccessoryService from "../services/accessory.service";

export const AccessoryContext = createContext();

const normalizeAccessoryOrdering = (ordering = []) => {
  if (typeof ordering === "string") {
    try {
      ordering = JSON.parse(ordering);
    } catch {
      return [];
    }
  }
  return Array.isArray(ordering) ? ordering : [];
};

export const selectAccessoriesForTab = (accessories = [], tab) =>
  normalizeAccessoryOrdering(tab?.ordering)
    .map((id) =>
      (accessories || []).find((item) => String(item.id) === String(id)),
    )
    .filter(Boolean);

export const appendAccessoryToTab = (tab, accessoryId) => ({
  ...(tab || {}),
  ordering: [
    ...normalizeAccessoryOrdering(tab?.ordering).filter(
      (id) => String(id) !== String(accessoryId),
    ),
    accessoryId,
  ],
});

export const upsertAccessory = (accessories = [], savedAccessory) => {
  if (!savedAccessory?.id) return accessories || [];

  const savedId = String(savedAccessory.id);
  const exists = (accessories || []).some(
    (item) => String(item.id) === savedId,
  );

  if (!exists) return [...(accessories || []), savedAccessory];
  return (accessories || []).map((item) =>
    String(item.id) === savedId
      ? { ...item, ...savedAccessory }
      : item,
  );
};

export const mergeSavedAccessory = (
  currentAccessory = {},
  submittedAccessory = {},
  responseAccessory = {},
) => ({
  ...(currentAccessory || {}),
  ...(responseAccessory || {}),
  ...(submittedAccessory || {}),
  id:
    responseAccessory?.id ??
    submittedAccessory?.id ??
    currentAccessory?.id,
  ...(responseAccessory?.account_id !== undefined
    ? { account_id: responseAccessory.account_id }
    : {}),
  ...(responseAccessory?.created_at !== undefined
    ? { created_at: responseAccessory.created_at }
    : {}),
  ...(responseAccessory?.updated_at !== undefined
    ? { updated_at: responseAccessory.updated_at }
    : {}),
  attachments:
    responseAccessory?.attachments ??
    submittedAccessory?.attachments ??
    currentAccessory?.attachments ??
    [],
  restrictions:
    responseAccessory?.restrictions ??
    submittedAccessory?.restrictions ??
    currentAccessory?.restrictions ??
    [],
  variants:
    responseAccessory?.variants ??
    submittedAccessory?.variants ??
    currentAccessory?.variants ??
    [],
});

export const AccessoryProvider = ({ children }) => {
  const [isNew, setIsNew] = useState(false);
  const [accessory, setAccessory] = useState(undefined);
  const [accessories, setAccessories] = useState([]);
  const [selectedTab, setSelectedTab] = useState(undefined);
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const accessoryMutationRevision = useRef(0);

  const contextAccessory = {
    isNew,
    setIsNew,
    accessory,
    setAccessory,
    selectedTab,
    setSelectedTab,
    total,
    accessories,
    tags,
    loading,
    isProcessing,
    setProcessing,

    initAccessory: () => {
      setAccessory({
        account_id: 0,
        label: "",
        category: "",
        description: "",
        specifications: "",
        price: "",
        availability: "99999",
        date_required: false,
        quantity_required: false,
        quantity_minimum: -1,
        quantity_maximum: -1,
        special_instructions_required: false,
        special_instructions: "",
        variants: [],
        ordering: [],
        attachments: [],
        tags: [],
      });
    },

    retrieve: async () => {
      setProcessing(true);
      try {
        const requestRevision = accessoryMutationRevision.current;
        const res = await AccessoryService.retrieve();
        if (requestRevision === accessoryMutationRevision.current) {
          setAccessories(res.data);
        }
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
      }
    },

    insertAccessory: async (obj) => {
      setProcessing(true);
      return AccessoryService.insertAccessory(obj)
        .then((res) => {
          setProcessing(false);
          accessoryMutationRevision.current += 1;
          const savedAccessory = mergeSavedAccessory({}, obj, res.data);
          setAccessory(savedAccessory);
          setAccessories((current) =>
            upsertAccessory(current, savedAccessory),
          );
          return [savedAccessory, null];
        })
        .catch((err) => {
          setProcessing(false);
          return [null, err];
        });
    },

    updateAccessory: async (obj) => {
      setProcessing(true);
      return AccessoryService.updateAccessory(obj)
        .then((res) => {
          setProcessing(false);
          accessoryMutationRevision.current += 1;
          const savedAccessory = mergeSavedAccessory(
            accessory,
            obj,
            res.data,
          );
          setAccessory(savedAccessory);
          setAccessories((current) =>
            upsertAccessory(current, savedAccessory),
          );
          return [savedAccessory, null];
        })
        .catch((err) => {
          setProcessing(false);
          console.log(err);
          return [null, err];
        });
    },

    deleteAccessory: async (id) => {
      setLoading(true);
      return AccessoryService.deleteAccessory(id)
        .then((res) => {
          accessoryMutationRevision.current += 1;
          setAccessories((current) =>
            current.filter((item) => String(item.id) !== String(id)),
          );
          setLoading(false);
          return ["success", null];
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
          return [null, err];
        });
    },

    updateTabOrder: async (id, ordering) => {
      setLoading(true);
      let resp = await AccessoryService.updateTabOrder(id, ordering);

      return resp.data;
    },
  };

  return (
    <AccessoryContext.Provider value={contextAccessory}>
      {children}
    </AccessoryContext.Provider>
  );
};
